import {
  messages,
  quickQuestionsHidden,
  aiThinking,
  activeLeadCard,
  lastLeadCard,
  pendingLeadCard,
  closeLeadCardWithReopenPill,
  digitalChatMode,
  videoState,
  inputDraft,
  appendMessage,
  hydrateFromHistory,
} from '../../store/widget-store';
import { getInitialSession, getWsClient, resetSession } from '../../services/ws-client';
import { getAudioPlayer } from '../../services/audio-player';
import type { VisitorMessagePayload } from '../../types';
import type { I18n } from '../../i18n';

/**
 * 数字人聊天的两个会话级动作,从 ChatPane 提取为共享函数:
 * 桌面端按钮在 ChatPane 的 header 内,移动端按钮在 ImmersiveShell 的顶部悬浮组里(与语言切换并排),
 * 两处共用同一份逻辑,避免行为漂移。操作的全是全局 signal / 单例服务,仅 i18n 的 t 需外部传入。
 */

/**
 * 向 AI 发一条文本:乐观追加访客气泡 + 标记「正在思考」+ 经 SSE 发出。
 * ChatPane 输入框发送与 WelcomePane 欢迎屏点引导问题共用同一份逻辑,避免两处行为漂移。
 * 仅负责 AI 链路核心:不碰 inputDraft / 输入框 DOM(那是 ChatPane 输入框专属收尾),也不处理人工 IM 分支。
 */
export function sendDigitalAiText(text: string): void {
  const trimmed = text.trim();
  if (!trimmed) return;
  // 防御:AI 回合进行中(思考/流式/口播)拒绝发送。UI 层按钮/回车已拦,
  // 这里兜底快捷问题等旁路;BE 另有 per-convId 回合串行做最终防线。
  if (
    aiThinking.value ||
    messages.value.some((m) => m.role === 'ai' && m.streaming === true)
  ) {
    return;
  }
  // 新回合开始:上一轮的留资卡若还开着,自动收起(留「再开」pill 锚在流内),
  // 避免卡片挂着错误位置横跨后续问答。
  closeLeadCardWithReopenPill();
  appendMessage({
    id: `m-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    role: 'visitor',
    text: trimmed,
    ts: Date.now(),
  });
  quickQuestionsHidden.value = true;
  aiThinking.value = true;
  const payload: VisitorMessagePayload = { text: trimmed, msgType: 'text' };
  getWsClient().emit('visitor.message', payload);
}

/** AI 客服 ⇄ 人工客服 切换(转人工 / 返回 AI)。 */
export function toggleDigitalChatMode(): void {
  const next: 'ai' | 'human' = digitalChatMode.value === 'ai' ? 'human' : 'ai';
  digitalChatMode.value = next;
  if (next === 'human') {
    // 人工招呼改由 ChatPane 顶部常驻招呼行渲染(人工聊天主体仅在坐席在线时渲染,
    // 招呼行天然只在接通后出现),不再往消息流追加「正在为您转接…请稍候」系统提示。
    // 转人工:停掉数字人语音、视频回 idle(真人消息不做 TTS、不驱动口型)
    getAudioPlayer().stopAll();
    videoState.value = 'idle';
  }
  // 返回 AI 不再 append web.aiGreeting:AI 模式 ChatPane 顶部已有常驻欢迎行承担欢迎。
}

/** 重置 AI 聊天:关旧会话、清空本地状态、重连并按新会话 history 水合。 */
export async function resetDigitalChat(t: I18n['t']): Promise<void> {
  getAudioPlayer().stopAll();
  await resetSession();
  messages.value = [];
  inputDraft.value = '';
  aiThinking.value = false;
  quickQuestionsHidden.value = false;
  activeLeadCard.value = null;
  lastLeadCard.value = null;
  pendingLeadCard.value = null;
  digitalChatMode.value = 'ai';
  const client = getWsClient();
  await client.connect();
  const sess = getInitialSession();
  if (sess) hydrateFromHistory(sess.history, sess.pendingCards, t('panel.leadReopenHint'));
}
